import React, { useState, useContext, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { MediaQueryContext } from '@arrive/responsive-provider';
import ReactFocusLock from 'react-focus-lock';

import { GET_LOCATIONS_ZONE } from 'api/queries';

import { LocationSearchType } from 'sharedTypes/location/locationTypes';

import LOCATION from '@arrive/ui-icons/svg/ic_location.svg';

import { components as defaultSelectComponents } from 'react-select';
import SearchableDropdown from '@arrive/searchable-dropdown';
import Popover from '@arrive/popover';
import { ApplyButtonBarMobile } from '@arrive/apply-button-bar';
import Button from '@arrive/button';
import TextEntry from '@arrive/text-entry';

import { prettifyIfLocationName } from 'helpers/bookingModalHelpers';

import styles from './BookingModal.scss';

type baseStylesType = { [key: string]: any };
const mobileSelectStyles = {
  container: (base: baseStylesType) => {
    return {
      ...base,
      margin: '10px'
    }
  },
  control: (base: baseStylesType) => {
    return {
      ...base,
      fontSize: '16px',
      boxShadow: 'none'
    };
  },
  menu: (base: baseStylesType) => {
    return {
      ...base,
      boxShadow: 'none'
    }
  },
  menuList: (base: baseStylesType) => {
    return {
      ...base,
      maxHeight: 'calc(100vh - 114px)'
    }
  }
};

export default function BookingModalCitySearch({ selectedCity, setSelectedCity }: { selectedCity: LocationSearchType; setSelectedCity: (newCity: LocationSearchType) => void; }) {
  const [inputValue, setInputValue] = useState('');
  const currentMediaTypes = useContext(MediaQueryContext);
  const [showMobilePopover, setShowMobilePopover] = useState(false);
  const [mobileSelectedCity, setMobileSelectedCity] = useState(selectedCity);
  const menuListRef = useRef();

  const { data = { getLocationsZone: { data: [] } }, loading } = useQuery(
    GET_LOCATIONS_ZONE,
    {
      variables: { searchInput: inputValue },
      context: {
        debounceKey: 'citySearchBIN'
      },
      skip: !inputValue || inputValue.length < 3
    }
  );
  const getLocationsZone = data.getLocationsZone || { data: [] };
  const options = getLocationsZone.data.map((selection: LocationSearchType) => ({
    ...selection,
    value: selection.Id || selection.Value,
    className: styles.listItem,
    iconClassName: styles.listIcon,
    label: prettifyIfLocationName(selection.Name),
    icon: LOCATION
  }));

  const searchableDropdownProps = {
    isMulti: false,
    inputValue,
    isLoading: loading,
    menuIsOpen: options && !!options.length,
    onInputChange: setInputValue,
    options,
    placeholder: 'Empty Location',
    className: styles.emptyCitySearchInputWrapper
  }

  if (currentMediaTypes.xs) {
    const onApplyClick = () => {
      setSelectedCity(mobileSelectedCity)
      setShowMobilePopover(false);
    };

    const handleClear = () => {
      setShowMobilePopover(false);
      setSelectedCity(null);
    };

    const WrappedMenuList = (props: any) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
      return <defaultSelectComponents.MenuList {...props} innerRef={menuListRef} />
    }

    return (
      <Popover
        showPopover={showMobilePopover}
        setShowPopover={setShowMobilePopover}
        screenSizeSmall
        scrollableChildRef={menuListRef}
        popperContent={(
          <ReactFocusLock>
            <ApplyButtonBarMobile onCancelClick={() => setShowMobilePopover(false)} title='Empty Location' onApplyClick={onApplyClick} />
            <SearchableDropdown
              {...searchableDropdownProps}
              autoFocus
              theme={theme => {
                return {
                  ...theme,
                  borderRadius: 4,
                  spacing: {
                    ...theme.spacing,
                    controlHeight: 36,
                    menuGutter: 0
                  }
                }
              }}
              selectStyles={mobileSelectStyles}
              suffix={<Button disabled={!mobileSelectedCity} text onClick={handleClear}>Clear</Button>}
              components={{
                MenuList: WrappedMenuList
              }}
              onChange={setMobileSelectedCity}
              value={mobileSelectedCity}
            />
          </ReactFocusLock>
        )}
      >
        <TextEntry
          data-testid='bookingModalXsEmptyLocationBtn'
          role='button'
          classes={{ inputWrapper: styles.emptyCitySearchInputWrapper, container: styles.emptyCityContainerResponsive }}
          onClick={() => setShowMobilePopover(!showMobilePopover)}
          onChange={() => {
            //no op
          }}
          label='Empty Location'
          value={selectedCity?.label || ''}
        />
      </Popover>
    )
  }

  return (
    <SearchableDropdown
      {...searchableDropdownProps}
      selectStyles={currentMediaTypes.sm ? {
        valueContainer: (style): any => ({
          ...style,
          paddingTop: 0,
          paddingBottom: 0,
          height: 38,
          alignContent: 'center'
        })
      } : undefined}
      value={selectedCity}
      onChange={setSelectedCity}
      menuPortalTarget={document.getElementById('portalTarget')}
    />
  )
}
